import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import type { WpArticle } from "@/lib/posts.types";
import type { ThemeKey } from "@/lib/theme-review";
import { themeByKey } from "@/lib/themes-public";
import { resizeImg } from "@/lib/img";
import { THEME_DOT, THEME_IMG, type SiteBook } from "./theme-ui";

export type ThemedPost = WpArticle & { theme?: ThemeKey | null };

export function SectionTitle({ id, title, aside, link }: { id?: string; title: string; aside?: React.ReactNode; link?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
      <h2 id={id} className="min-w-0 max-w-full text-balance font-serif text-2xl font-medium lg:text-[30px]">{title}</h2>
      {aside ? <p className="max-w-xl text-sm leading-snug text-ink/70">{aside}</p> : <span aria-hidden className="hidden h-px flex-1 bg-ink/15 sm:block" />}
      {link && <span className="ml-auto">{link}</span>}
    </div>
  );
}

export function ThemeKicker({ theme, date, className = "" }: { theme?: ThemeKey | null; date?: string; className?: string }) {
  const t = themeByKey(theme);
  return (
    <span className={`flex flex-wrap items-center gap-x-2 text-[10px] uppercase tracking-[0.2em] text-ink/75 ${className}`}>
      {t && <span aria-hidden className={`size-2 rounded-full ${THEME_DOT[t.key]}`} />}
      {t && <span>{t.title}</span>}
      {t && date && <span aria-hidden>·</span>}
      {date && <span className="normal-case tracking-normal">{date}</span>}
    </span>
  );
}

function Thumb({ p, className }: { p: ThemedPost; className: string }) {
  const src = p.img ? resizeImg(p.img, 240, { ar: 16 / 10 }) : p.theme ? THEME_IMG[p.theme] : null;
  return src ? (
    <img src={src} alt="" loading="lazy" decoding="async" width={240} height={150} className={`object-cover ${className}`} />
  ) : (
    <span aria-hidden className={`block bg-muted ${className}`} />
  );
}

/** Row: thumbnail · (kicker) date · title · arrow — used by theme and article pages. */
export function PostRows({ posts, showTheme = false, size = "md" }: { posts: ThemedPost[]; showTheme?: boolean; size?: "sm" | "md" }) {
  return (
    <ul className="divide-y divide-ink/10">
      {posts.map((p) => (
        <li key={p.slug}>
          <Link to="/yazilar/$slug" params={{ slug: p.slug }} className="group grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-4 py-3">
            <Thumb p={p} className={size === "sm" ? "h-14 w-14" : "h-14 w-20 sm:h-[60px] sm:w-[100px]"} />
            <span className="min-w-0">
              {showTheme ? <ThemeKicker theme={p.theme} date={p.date} /> : <span className="block text-xs text-ink/65">{p.date}</span>}
              <span className={`mt-1 block font-serif leading-snug text-ink group-hover:text-clay ${size === "sm" ? "text-[15px] sm:text-[16px]" : "text-[16px] sm:text-[19px]"}`}>{p.title}</span>
            </span>
            <ArrowRight aria-hidden className="size-4 shrink-0 text-ink transition-transform group-hover:translate-x-1" />
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function BookCard({ book }: { book: SiteBook }) {
  return (
    <aside aria-labelledby="ilgili-kitap" className="bg-secondary p-5 sm:p-6">
      <h2 id="ilgili-kitap" className="font-serif text-xl font-medium lg:text-[22px]">İlgili Kitap</h2>
      <div className="mt-4 grid grid-cols-[88px_minmax(0,1fr)] gap-4 sm:grid-cols-[120px_minmax(0,1fr)] lg:grid-cols-1">
        <img src={book.cover} alt={book.title} loading="lazy" width={240} height={360} className="aspect-[2/3] w-full object-cover shadow-sm lg:mx-auto lg:w-40" />
        <div className="min-w-0">
          <p className="font-serif text-[18px] leading-snug">{book.title}</p>
          <p className="mt-2 hidden text-sm leading-relaxed text-ink/75 sm:block">{book.summary}</p>
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <Link to={book.to as any} className="mt-3 inline-flex min-h-11 items-center gap-2 font-serif text-[15px] text-theme-tech hover:underline">
            Kitabı keşfet <ArrowRight aria-hidden className="size-4" />
          </Link>
        </div>
      </div>
    </aside>
  );
}

/** "Fikirler Arasında": cross-theme links from approved theme data. */
export function IdeasBetween({ posts, intro }: { posts: ThemedPost[]; intro: string }) {
  if (!posts.length) return null;
  return (
    <section aria-labelledby="fikirler-arasinda" className="border-t border-ink/10">
      <div className="mx-auto max-w-7xl px-5 py-8 sm:px-8 lg:py-12">
        <SectionTitle id="fikirler-arasinda" title="Fikirler Arasında" aside={intro} />
        <ul className="mt-5 grid divide-y divide-ink/10 md:grid-cols-3 md:divide-x md:divide-y-0 lg:mt-8">
          {posts.map((p) => (
            <li key={p.slug} className="md:px-6 md:first:pl-0 md:last:pr-0">
              <Link to="/yazilar/$slug" params={{ slug: p.slug }} className="group grid grid-cols-[56px_minmax(0,1fr)_auto] items-start gap-x-4 py-4 sm:grid-cols-[80px_minmax(0,1fr)_auto] md:py-0">
                <span className="block aspect-square overflow-hidden rounded-full bg-muted">
                  {p.theme && <img src={THEME_IMG[p.theme]} alt="" loading="lazy" width={160} height={160} className="h-full w-full object-cover" />}
                </span>
                <span className="min-w-0">
                  <ThemeKicker theme={p.theme} />
                  <span className="mt-1.5 block font-serif text-[16px] leading-snug text-ink group-hover:text-clay sm:text-[19px]">{p.title}</span>
                  {p.excerpt && <span className="mt-2 hidden text-sm leading-snug text-ink/70 lg:line-clamp-3">{p.excerpt}</span>}
                </span>
                <ArrowRight aria-hidden className="mt-1 size-4 text-ink transition-transform group-hover:translate-x-1" />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/** One post per other theme that shares this theme (primary/secondary), newest first. */
export function pickIdeasBetween(
  posts: WpArticle[],
  themeOf: Map<string, { primary: ThemeKey; secondary: ThemeKey | null }>,
  focus: ThemeKey,
  exclude: Set<string>,
): ThemedPost[] {
  const out: ThemedPost[] = [];
  const order: ThemeKey[] = ["planet", "tech", "economy", "meaning"];
  for (const other of order.filter((k) => k !== focus)) {
    const hit = posts.find((p) => {
      if (exclude.has(p.slug)) return false;
      const t = themeOf.get(p.slug);
      return t && ((t.primary === other && t.secondary === focus) || (t.primary === focus && t.secondary === other));
    });
    if (hit) {
      exclude.add(hit.slug);
      out.push({ ...hit, theme: themeOf.get(hit.slug)!.primary === focus ? other : themeOf.get(hit.slug)!.primary });
    }
  }
  return out.slice(0, 3);
}
